import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import ScoreCircularProgressV2 from 'src/common/config/ScoreCircularProgressV2';
import { CustomTooltip } from 'src/content/keywordResearch/CustomTooltip';
import { newCardsCss } from 'src/content/dashboard/DashboardCss';
import { keywordResearchDashBoardCss } from 'src/content/keywordResearch/keywordResearchCSS';
import { KeywordOverviewResultCardsCss } from 'src/content/keywordResearch/keywordOverview/keywordOverviewCSS';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { LocalSEOAction } from '../types';
import { updateLocalSEOBusinessDetails } from 'src/common/Scraper';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { setOrbitAISnackBar } from 'src/redux/siteAuditReducer';
import { useGetReviewAutomationSettings } from 'src/content/orbitAi/review-automation/hooks/useGetReviewAutomationSettings';
import { usePagination } from '../../content-strategy/hooks/usePagination';

export interface ILocalRankingInsightsCard{
  rankingInsights: LocalSEOAction[];
  accountId?: string;
  locationId?: string;
  healthScore?: {
    value: number;
    change: number;
  }
}

export default function LocalRankingInsightsCard({ rankingInsights, accountId, locationId, healthScore }: ILocalRankingInsightsCard) {
  const navigate = useNavigate();
  const [applyingTaskId, setApplyingTaskId] = useState<string | null>(null);
  const [selectedDetailsAction, setSelectedDetailsAction] = useState<LocalSEOAction | null>(null);
  const [actionToConfirm, setActionToConfirm] = useState<LocalSEOAction | null>(null);
  const [reviewAutomationDialogOpen, setReviewAutomationDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { data: reviewAutomationSettings } = useGetReviewAutomationSettings({
    accountId: accountId || "",
    locationId: locationId || "",
  });

  const handleApplyClick = (info: LocalSEOAction) => {
    if (info.taskId === 'replyToReviews') {
      const isReviewAutomationEnabled = reviewAutomationSettings?.settings?.enabled ?? false;
      if (isReviewAutomationEnabled) {
        navigate('/orbit-ai-agent/review-automation');
      } else {
        setReviewAutomationDialogOpen(true);
      }
    } else {
      setActionToConfirm(info);
    }
  };

  const handleApply = async (info: LocalSEOAction): Promise<boolean> => {
    if (!info.postPayload) return false;
    setApplyingTaskId(info.taskId);
    try {
      const response = await updateLocalSEOBusinessDetails(info.postPayload);
      if (response?.status === 'success' || response?.status === 200 || response?.data?.status === 'success') {
        queryClient.invalidateQueries({ queryKey: ["getLocalSEOReport"] });
        dispatch(setOrbitAISnackBar({
          status: 'success',
          message: response?.message || 'Changes applied successfully'
        }));
        return true;
      } else {
        console.error('Failed to apply details:', response);
        dispatch(setOrbitAISnackBar({
          status: 'error',
          message: response?.message || 'Something went wrong. Please try again.'
        }));
        return false;
      }
    } catch (error: any) {
      console.error('Error applying details:', error);
      dispatch(setOrbitAISnackBar({
        status: 'error',
        message: error?.message || 'Something went wrong. Please try again.'
      }));
      return false;
    } finally {
      setApplyingTaskId(null);
    }
  };

  const {
    currentItems: displayedItems,
    page,
    totalPages,
    handlePrev,
    handleNext,
    isFirstPage,
    isLastPage
  } = usePagination(rankingInsights || [], 3);

  return (
    <div className="bg-white border border-[#EAECF0] rounded-xl shadow-sm p-6 w-full pb-0">
      <div className="flex justify-between items-center mb-4">
        <Box className="flex items-center gap-3">
          <Typography sx={newCardsCss?.headTextCss}>
            Local Ranking Insights
          </Typography>
          <CustomTooltip
            title={
              <Box>
                <Typography sx={keywordResearchDashBoardCss?.customTollTipText}>
                  Overview of your Google Business Profile, including listing
                  details, categories, and optimization status.
                </Typography>
              </Box>
            }
            placement="bottom"
            arrow
          >
            <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
          </CustomTooltip>
        </Box>
      </div>

      <div className="flex flex-col lg:flex-row items-center gap-8">
        {/* Health Score Progress Circle */}
        <div className="flex-shrink-0 flex items-center justify-center w-full lg:w-fit lg:pr-8">
          <ScoreCircularProgressV2 value={healthScore.value}>
            <ScoreCircularProgressV2.Title title="Score" className="" />
            <ScoreCircularProgressV2.Value
              value={healthScore.value}
              className=""
              scoreClass="!text-3xl"
            />
            <ScoreCircularProgressV2.Trend trendValue={healthScore.change} className="mt-1" />
          </ScoreCircularProgressV2>
        </div>

        {/* Other Four Stats */}
        <div className="w-full">
          <div className="flex justify-between items-center px-6 py-5 pr-0 border-b border-[#E0E0E6]">
            <div>
              <p className="text-base font-medium text-[#475467]">
                Action Items
              </p>
            </div>

            <div className="flex items-center">
              <p className="text-primary text-base font-medium">
                {page}/{totalPages}
              </p>
              <IconButton
                onClick={handlePrev}
                disabled={isFirstPage}
                size="small"
                sx={{ p: 1 }}
              >
                <ArrowBackIosNewIcon
                  sx={{
                    fontSize: 16,
                    color:
                      isFirstPage ? '#CBD5E1' : 'var(--color-primary)'
                  }}
                />
              </IconButton>
              <IconButton
                onClick={handleNext}
                disabled={isLastPage}
                size="small"
                sx={{ p: 1 }}
              >
                <ArrowForwardIosIcon
                  sx={{
                    fontSize: 16,
                    color:
                      isLastPage
                        ? '#CBD5E1'
                        : 'var(--color-primary)'
                  }}
                />
              </IconButton>
            </div>
          </div>
          {displayedItems.map((info, index) => (
            <div
              key={index}
              className={[
                `pl-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full`,
                index === displayedItems.length - 1
                  ? ''
                  : 'border-b border-[#E0E0E6]'
              ].join(' ')}
            >
              <div className="flex items-center">
                <p className="text-[#2B2F36] font-semibold text-base">
                  {info.title}
                </p>
              </div>

              <div className="flex flex-wrap sm:flex-nowrap justify-start sm:justify-end items-center gap-3">
                {/* {info.isAIRecommended && (
                  <div className='flex justify-end items-center gap-2'>
                    <img src={"/static/images/icons/ai_visibility.svg"} alt="" />
                    <p className='text-xs font-normal'>AI Recommended</p>
                  </div>
                )} */}
                <p
                  className={[
                    `text-xs font-medium px-2 py-0.5 rounded-2xl whitespace-nowrap`,
                    info.impact === 'High'
                      ? 'text-[#027A48] bg-[#ECFDF3]'
                      : info.impact === 'Medium'
                      ? 'text-[#B54708] bg-[#FFFAEB]'
                      : 'text-[#344054] bg-[#F2F4F7]'
                  ].join(' ')}
                >
                  {info.impact} Impact
                </p>
                <Button
                  variant="outlined"
                  className="px-4 h-10 !border-[#A09DAB] !text-[#120242] !normal-case !font-semibold"
                  onClick={() => setSelectedDetailsAction(info)}
                >
                  Details
                </Button>
                 <CustomTooltip
                  title={
                    (!info.canPost && info.taskId !== 'replyToReviews') && info.toolTipText ? (
                      <Typography sx={{ ...keywordResearchDashBoardCss?.customTollTipText, width: 500 }}>
                        {info.toolTipText}
                      </Typography>
                    ) : (
                      ''
                    )
                  }
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none !important'
                      }
                    }
                  }}
                  PopperProps={{
                    sx: {
                      '& .MuiTooltip-arrow': {
                        left: 'calc(100% - 60px) !important',
                        transform: 'none !important'
                      }
                    }
                  }}
                  arrow
                  placement="top-end"
                >
                  <span>
                    <Button
                      className='w-24 min-w-24 h-10'
                      variant='contained'
                      disabled={(!info.canPost && info.taskId !== 'replyToReviews') || applyingTaskId !== null}
                      onClick={() => handleApplyClick(info)}
                    >
                      Apply
                    </Button>
                  </span>
                </CustomTooltip>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Dialog
        open={selectedDetailsAction !== null}
        onClose={() => setSelectedDetailsAction(null)}
        disableScrollLock
      >
        <div className="p-6 min-w-[500px] max-w-[600px] relative">
          <p className="text-primary font-semibold text-xl mb-4">
            {selectedDetailsAction?.title}
          </p>

          <div className="mb-2 bg-[#F9FAFB] p-3 rounded-lg border border-[#EAECF0]">
            <p className="text-[#344054] font-semibold text-sm mb-1">
              Description
            </p>
            <p className="text-[#475467] text-sm">
              {selectedDetailsAction?.description}
            </p>
          </div>

          {selectedDetailsAction?.suggestion && (
            <div className="bg-[#F9FAFB] p-3 rounded-lg border border-[#EAECF0]">
              <p className="text-[#344054] font-semibold text-sm mb-1">
                Suggestion
              </p>
              <p className="text-[#475467] text-sm">
                {selectedDetailsAction.suggestion}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outlined"
              className="w-24 px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
              onClick={() => setSelectedDetailsAction(null)}
            >
              Close
            </Button>
            {(selectedDetailsAction?.canPost || selectedDetailsAction?.taskId === 'replyToReviews') && (
              <Button
                className="h-10 px-6 w-24"
                variant="contained"
                disabled={applyingTaskId !== null}
                onClick={() => {
                  const action = selectedDetailsAction;
                  setSelectedDetailsAction(null);
                  handleApplyClick(action);
                }}
              >
                Apply
              </Button>
            )}
          </div>

          <div aria-label="dialog-close-btn" className="absolute top-0 right-0">
            <IconButton
              aria-label="close"
              className="hover:rounded-lg"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                height: '40px',
                padding: '8px'
              }}
              onClick={() => setSelectedDetailsAction(null)}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={actionToConfirm !== null}
        onClose={() => {
          if (applyingTaskId === null) {
            setActionToConfirm(null);
          }
        }}
        disableScrollLock
      >
        <div className="p-6 min-w-[400px] max-w-[500px] relative">
          <p className="text-primary font-semibold text-xl mb-4">
            Confirm Changes
          </p>
          <p className="text-sm text-[#475467] mb-6">
            Are you sure you want to apply the changes for <strong>{actionToConfirm?.title}</strong>? Please note that it may take some time for these changes to reflect on your Google Business Profile.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outlined"
              className="w-24 px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
              disabled={applyingTaskId !== null}
              onClick={() => setActionToConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="h-10 px-6 w-24 min-w-24"
              disabled={applyingTaskId !== null}
              onClick={async () => {
                if (actionToConfirm) {
                  await handleApply(actionToConfirm);
                  setActionToConfirm(null);
                }
              }}
            >
              {applyingTaskId !== null ? <CircularProgress size={16} color="inherit" /> : 'Confirm'}
            </Button>
          </div>
        </div>
      </Dialog>
      <Dialog
        open={reviewAutomationDialogOpen}
        onClose={() => setReviewAutomationDialogOpen(false)}
        disableScrollLock
      >
        <div className="p-6 min-w-[400px] max-w-[500px] relative">
          <p className="text-primary font-semibold text-xl mb-4">
            Enable Review Automation
          </p>
          <p className="text-sm text-[#475467] mb-6">
            Enable review automation to auto-reply unanswered reviews.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outlined"
              className="w-24 px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
              onClick={() => setReviewAutomationDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="h-10 px-6 !normal-case !font-semibold"
              onClick={() => {
                setReviewAutomationDialogOpen(false);
                navigate('/orbit-ai-agent/review-automation');
              }}
            >
              Go to Review Automation
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
